import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Mail, RefreshCw, X, Clock, User, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const PendingInvitationsCard = ({ onUpdate }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${API}/team-invitations?status=pending`);
      setInvitations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    }
  };

  const handleResend = async (invitationId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/team-invitations/${invitationId}/resend`);
      toast.success("Invitation resent successfully");
      fetchInvitations();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error resending invitation:", error);
      toast.error("Failed to resend invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (invitationId) => {
    if (!window.confirm("Are you sure you want to cancel this invitation?")) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/team-invitations/${invitationId}`);
      toast.success("Invitation cancelled");
      fetchInvitations();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      toast.error("Failed to cancel invitation");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getExpiryCountdown = (expiresAt) => {
    if (!expiresAt) return "Never expires";
    
    const expiry = new Date(expiresAt);
    const now = new Date();
    
    if (expiry < now) {
      return "Expired";
    }
    
    const diffMs = expiry - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 1) {
      return `${diffDays} days remaining`;
    } else if (diffHours > 1) {
      return `${diffHours} hours remaining`;
    } else {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins} minutes remaining`;
    }
  };

  const getExpiryColor = (expiresAt) => {
    if (!expiresAt) return "text-gray-500";
    
    const expiry = new Date(expiresAt);
    const now = new Date();
    
    if (expiry < now) return "text-red-600 font-medium";
    
    const diffDays = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 1) return "text-orange-600 font-medium";
    if (diffDays <= 3) return "text-yellow-600";
    return "text-green-600";
  };

  if (invitations.length === 0) {
    return null;
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="h-5 w-5" />
          Pending Invitations ({invitations.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {invitations.map((invitation) => (
            <div
              key={invitation.id}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <span className="font-medium">{invitation.email}</span>
                  {invitation.full_name && (
                    <span className="text-sm text-slate-600">({invitation.full_name})</span>
                  )}
                </div>
                
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                  <span>Role: {invitation.role_name || "Not assigned"}</span>
                  {invitation.subsidiary_names.length > 0 && (
                    <span>• Subsidiaries: {invitation.subsidiary_names.join(", ")}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <Clock className="h-3 w-3 text-slate-400" />
                  <span>Sent: {formatDate(invitation.created_at)}</span>
                </div>
                
                <div className={`flex items-center gap-2 mt-1 text-xs ${getExpiryColor(invitation.expires_at)}`}>
                  {isExpired(invitation.expires_at) ? (
                    <>
                      <AlertCircle className="h-3 w-3" />
                      <span>Expired {formatDate(invitation.expires_at)}</span>
                    </>
                  ) : (
                    <>
                      <Clock className="h-3 w-3" />
                      <span>{getExpiryCountdown(invitation.expires_at)}</span>
                    </>
                  )}
                  <span>• Invited by: {invitation.invited_by_name}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                  Pending
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleResend(invitation.id)}
                  disabled={loading}
                  title="Resend invitation"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCancel(invitation.id)}
                  disabled={loading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="Cancel invitation"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingInvitationsCard;
